import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { cityFormSchema, type CityFormValues } from './cityFormSchema'
import type { City, CityUpsert } from '../types'

interface Props {
  visible: boolean
  initial: City | null
  /** Seçili ülkenin adı — form başlığında bağlamı gösterir (ör. "Yeni Şehir — Türkiye"). */
  countryName?: string
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: CityUpsert) => void
  onHide: () => void
}

/** Şehir ekle/düzenle formu (026). TR ad zorunlu; EN opsiyonel. Sıra ve ülke formda yok. */
export default function CityFormDialog({
  visible,
  initial,
  countryName,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(cityFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: CityFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  const baseTitle = initial
    ? t('location.cities.form.editTitle')
    : t('location.cities.form.newTitle')
  const header = countryName ? `${baseTitle} — ${countryName}` : baseTitle

  return (
    <FormDialog
      visible={visible}
      header={header}
      submitLabel={t('location.cities.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t('location.cities.form.nameTr')}
          required
          autoFocus
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('location.cities.form.nameEn')}
        />
      </div>
    </FormDialog>
  )
}
